import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ProcessStatus } from 'src/app/core/models/process.model';
import { URLBuilder } from 'src/app/core/utils/url-builder';
import { ProcessService } from 'src/app/features/process/services/process.service';
import { AuthService } from 'src/app/core/services/AuthService/auth.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CacheService } from 'src/app/core/services/CacheService/cache.service';
import { NumberReservationService } from 'src/app/features/document/services/NumberReservation/number-reservation.service';

interface Contributor {
  id: number;
  nome: string;
}

interface Property {
  id: number;
  nome: string;
}

interface ThemeResponse {
  listaObj: { id: number; nome: string }[];
}

interface AuthResponse {
  listaObj: { token: string }[];
}
@Component({
  selector: 'app-process-dialog',
  templateUrl: './process-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSelectModule,
    MatDialogModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatSnackBarModule,
  ],
})
export class ProcessDialogComponent implements OnInit {
  processForm!: FormGroup;
  currentDate: string = new Date().toISOString();
  contributors: Contributor[] = [];
  properties: Property[] = [];
  availableThemes: { id: number; nome: string }[] = [];
  availableOpeningThemes: { id: number; nome: string }[] = [];
  isLoading = false;
  private urlBuilder = new URLBuilder();
  private readonly CACHE_TTL = 300; // Consistent TTL in seconds

  constructor(
    private readonly dialogRef: MatDialogRef<ProcessDialogComponent>,
    private readonly fb: FormBuilder,
    private readonly processService: ProcessService,
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly snackBar: MatSnackBar,
    private readonly cacheService: CacheService,
    private numberReservationService: NumberReservationService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      processTypeName: string;
      processTypeId: number;
      dueDays: number;
    }
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (!this.data.processTypeName || !this.data.dueDays) {
      console.error('Dados necessários não recebidos!');
      return;
    }

    this.processForm.patchValue({
      processType: this.data.processTypeName,
      dueDays: this.data.dueDays,
    });

    this.loadInitialData();
  }

  private initializeForm(): void {
    this.processForm = this.fb.group({
      processType: [
        { value: this.data.processTypeName, disabled: true },
        Validators.required,
      ],
      dueDays: [
        { value: this.data.dueDays, disabled: true },
        Validators.required,
      ],
      empresa: ['', Validators.required],
      propertyRegister: ['', Validators.required],
      descricao: ['', Validators.required],
      theme: ['', Validators.required],
      group: ['', Validators.required],
      registerDate: [
        { value: this.currentDate, disabled: true },
        Validators.required,
      ],
      status: [
        { value: ProcessStatus.Aguardando, disabled: true },
        Validators.required,
      ],
    });
  }

  private async loadInitialData(): Promise<void> {
    this.setLoadingState(true);
    try {
      await Promise.allSettled([
        this.loadContributors(),
        this.loadProperties(),
        this.loadAvailableThemes(),
        this.loadAvailableOpeningThemes(),
      ]);
    } finally {
      this.setLoadingState(false);
    }
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  private async loadContributors(): Promise<void> {
    const cacheKey = 'contributors';
    const cachedData = this.cacheService.getFromCache(cacheKey);

    if (cachedData) {
      this.contributors = cachedData;
      return;
    }

    try {
      const token = await this.getToken();
      if (!token) return;

      const url = this.urlBuilder.buildEndpoint('service/contribuintes/listar');
      const response = await firstValueFrom(
        this.http.get<ThemeResponse>(url, { headers: this.getHeaders(token) })
      );

      this.contributors = response?.listaObj || [];
      this.cacheService.setCache(cacheKey, this.contributors, this.CACHE_TTL);
    } catch (err) {
      this.handleError('Carregando Contribuintes')(err);
    }
  }

  private async loadProperties(): Promise<void> {
    const cacheKey = 'properties';
    const cachedData = this.cacheService.getFromCache(cacheKey);

    if (cachedData) {
      this.properties = cachedData;
      return;
    }

    try {
      const token = await this.getToken();
      if (!token) return;

      const url = this.urlBuilder.buildEndpoint('service/imoveis/listar');
      const response = await firstValueFrom(
        this.http.get<ThemeResponse>(url, { headers: this.getHeaders(token) })
      );

      this.properties = response?.listaObj || [];
      this.cacheService.setCache(cacheKey, this.properties, this.CACHE_TTL);
    } catch (err) {
      // Ensure properties is always an array
      this.properties = this.getCachedData<Property[]>(cacheKey) || [];
      this.handleError('Carregando Imóveis')(err);
    }
  }

  private async loadAvailableThemes(): Promise<void> {
    const cacheKey = 'availableThemes';
    const cachedData = this.cacheService.getFromCache(cacheKey) ?? []; // Fallback to empty array

    if (cachedData.length > 0) {
      this.availableThemes = cachedData;
      return;
    }

    try {
      const token = await this.getToken();
      if (!token) return;

      const url = this.urlBuilder.buildEndpoint('service/processo/modalidades');
      const response = await firstValueFrom(
        this.http.get<ThemeResponse>(url, { headers: this.getHeaders(token) })
      );

      this.availableThemes = response?.listaObj || [];
      this.cacheService.setCache(
        cacheKey,
        this.availableThemes,
        this.CACHE_TTL
      );
    } catch (err) {
      this.availableThemes = this.getCachedData('availableThemes') ?? []; // Ensure array
      this.handleError('Carregando tipos de processos')(err);
    }
  }

  private async loadAvailableOpeningThemes(): Promise<void> {
    const cacheKey = 'availableOpeningThemes';
    const cachedData = this.cacheService.getFromCache(cacheKey) ?? []; // Fallback to empty array

    if (cachedData.length > 0) {
      this.availableOpeningThemes = cachedData;
      return;
    }

    try {
      const token = await this.getToken();
      if (!token) return;

      const url = this.urlBuilder.buildEndpoint(
        'service/processo/modalidades/abertura'
      );
      const response = await firstValueFrom(
        this.http.get<ThemeResponse>(url, { headers: this.getHeaders(token) })
      );

      this.availableOpeningThemes = response?.listaObj || [];
      this.cacheService.setCache(
        cacheKey,
        this.availableOpeningThemes,
        this.CACHE_TTL
      );
    } catch (err) {
      this.availableOpeningThemes =
        this.getCachedData('availableOpeningThemes') ?? []; // Ensure array
      this.handleError(
        'Carregando modalidades disponíveis para abertura de processos'
      )(err);
    }
  }

  private async getToken(): Promise<string | null> {
    try {
      const token = await this.authService.getTokenAsync();
      if (!token || this.authService.isTokenExpired(token)) {
        throw new Error('O token é inválido ou expirou.');
      }
      return token;
    } catch (error) {
      this.handleError('Recuperação de token')(error);
      return null;
    }
  }

  private getHeaders(token: string): { [header: string]: string } {
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  private getCachedData<T>(key: string): T | null {
    const storedValue = localStorage.getItem(key);
    return storedValue ? (JSON.parse(storedValue) as T) : null;
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.processForm.get(controlName)?.hasError(errorName) ?? false;
  }

  async onSubmit(): Promise<void> {
    if (this.processForm.invalid) {
      this.processForm.markAllAsTouched();
      return;
    }
  
    try {
      // Reserve a number for the new process using the mocked service
      const processNumber = await firstValueFrom(
        this.numberReservationService.reserveNumber('Process')
      );
  
      const formData = this.processForm.getRawValue();
      const completeProcess = {
        ...formData,
        processTypeName: this.data.processTypeName,
        processTypeId: this.data.processTypeId,
        dueDays: this.data.dueDays,
        status: ProcessStatus.Aguardando,
        registerDate: this.currentDate,
        numeroProcesso: processNumber, // Add the reserved number
      };
  
      console.log('Submitting process data:', completeProcess);
      this.dialogRef.close(completeProcess);
    } catch (error) {
      console.error('Error reserving process number:', error);
      this.snackBar.open(
        'Erro ao gerar número do processo. Tente novamente.',
        'Fechar',
        { duration: 3000 }
      );
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      this.snackBar.open(
        `${operation} falhou. Verifique sua conexão e tente novamente.`,
        'Close',
        { duration: 3000 }
      );
      return of(result as T);
    };
  }
}
